import * as React from "react";

import { cn } from "@/lib/utils";

export type TypographyVariant = keyof typeof variantMap;

export type TypographyProps = {
  variant?: TypographyVariant;
  component?: React.ElementType;
  className?: string;
  children?: React.ReactNode;
};

export const variantMap = {
  h1: "text-h1 text-primary",
  "h1-xl":
    "text-h1-xl text-[2rem] font-semibold text-center m-auto max-[350px]:text-h1",
  h2: "text-h2",
  h3: "text-h3",
  h4: "text-h4",
  h5: "text-h5",
  h6: "text-h6",
  p: "text-base",
  subtitle: "text-sm font-medium text-subtext-grey leading-6 md:leading-5",
  subtext: "text-subtext-grey text-sm",
  disclaimer: "text-primary-70 text-sm",
  "uppercase-subtext":
    "text-subtext-all-blue text-base uppercase tracking-widest",
  eyebrow:
    "text-subtext-all-blue text-base uppercase tracking-widest text-xs text-center",
  "card-title": "text-subtext-grey uppercase tracking-widest text-sm",
  badge: "text-xs leading-2",
  "secondary-text": "leading-4 text-primary-70 text-xs font-normal",
  price: "text-2xl font-semibold tracking-normal",
  "price-xs": "text-lg font-semibold tracking-normal",
  uppercase: "uppercase tracking-widest",
} as const;

export type HTMLTextElement =
  | HTMLParagraphElement
  | HTMLSpanElement
  | HTMLHeadingElement
  | HTMLDivElement;

export const Typography = React.forwardRef<HTMLTextElement, TypographyProps>(
  ({ className, component, variant, children, ...props }, ref) => {
    const Component = component ?? "span";
    return (
      <Component
        className={cn(
          variantMap[variant ?? (component as TypographyVariant) ?? "p"],
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Component>
    );
  },
);
Typography.displayName = "Typography";
